import {
  AlertCircleIcon,
  BookIcon,
  ChevronIcon,
  CrossIcon,
} from 'assets/icons';
import { ImportableFile, UnsupportedFile } from 'helpers/import-types';
import { formatBytes } from 'helpers/storage';
import { classnames } from 'helpers/utils';
import { ReactNode, useState } from 'react';
import {
  albumAutoRange,
  albumFiles,
  assignedFiles,
  Destination,
  ExistingAlbum,
  fileDate,
  resolveDestination,
  ReviewAction,
  ReviewAlbum,
  ReviewFile,
  ReviewState,
  ReviewUnsortedFile,
  UNSORTED,
} from '../../review-model';
import {
  Checkbox,
  DateCell,
  DestinationOptions,
  DestinationPicker,
  fileKind,
  FileThumb,
  KIND_LABEL,
  RangeChip,
  SetAllDates,
} from './review-controls';
import styles from './review.module.scss';
import layout from './review-layout.module.scss';

type Dispatch = (action: ReviewAction) => void;

/** What a card needs to let unsorted files choose (or create) an album. */
type Sorting = {
  existingAlbums: ExistingAlbum[];
  onCreateAlbum: (name: string, fileKeys: string[]) => void;
};

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

// "photo.WEBP" → "webp"; null when the name has no extension.
const extensionOf = (name: string) => {
  const dot = name.lastIndexOf('.');
  return dot > 0 && dot < name.length - 1
    ? name.slice(dot + 1).toLowerCase()
    : null;
};

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

// A file the import can't bring in, listed where it sits. Same row as a file
// that can be imported, on red, with a box that can't be ticked and a date
// that can't be set.
const UnsupportedRow = ({ file }: { file: UnsupportedFile }) => {
  const extension = extensionOf(file.name);
  const meta = [
    extension ? `.${extension} isn’t supported` : 'Format not supported',
  ];
  if (file.size) meta.push(formatBytes(file.size));
  return (
    <div className={classnames(styles.row, styles.rowUnsupported)}>
      <Checkbox
        checked={false}
        disabled
        label={`${file.name} can’t be imported`}
      />
      <FileThumb
        file={{
          id: file.id,
          name: file.name,
          mimeType: 'application/octet-stream',
          size: file.size,
        }}
      />
      <div className={styles.fileText}>
        <span className={styles.fileName} title={file.name}>
          {file.name}
        </span>
        <span className={styles.fileMeta}>{meta.join(' · ')}</span>
      </div>
      <DateCell
        date={null}
        source="none"
        edited={false}
        fileName={file.name}
        disabled
        onChange={() => undefined}
      />
    </div>
  );
};

// A card's files that can't be imported, below the ones that can.
const UnsupportedRows = ({
  files,
  labelled,
}: {
  files: UnsupportedFile[];
  labelled: boolean;
}) =>
  files.length === 0 ? null : (
    <>
      {labelled && <div className={styles.rowsLabel}>Can’t be imported</div>}
      {files.map((file) => (
        <UnsupportedRow key={file.id} file={file} />
      ))}
    </>
  );

const FileRow = ({
  file,
  scope,
  dispatch,
  destination,
}: {
  file: ReviewFile;
  scope: string;
  dispatch: Dispatch;
  destination?: ReactNode;
}) => {
  const { item } = file;
  const meta = [KIND_LABEL[fileKind(item.mimeType)]];
  if (item.size) meta.push(formatBytes(item.size));
  return (
    <div
      className={classnames(styles.row, {
        [styles.rowWithDestination]: !!destination,
        [styles.rowOff]: !file.included,
      })}
    >
      <Checkbox
        checked={file.included}
        onChange={() =>
          dispatch({ type: 'toggleFile', scope, fileKey: file.key })
        }
        label={`Import ${item.name}`}
      />
      <FileThumb file={item} />
      <div className={styles.fileText}>
        <span className={styles.fileName} title={item.name}>
          {item.name}
        </span>
        <span className={styles.fileMeta}>{meta.join(' · ')}</span>
      </div>
      <DateCell
        date={fileDate(file)}
        source={file.detected.source}
        edited={file.override != null}
        fileName={item.name}
        disabled={!file.included}
        onChange={(date) =>
          dispatch({ type: 'setFileDate', scope, fileKey: file.key, date })
        }
      />
      {destination && <div className={styles.destination}>{destination}</div>}
    </div>
  );
};

// "Select all" + "Set one date for all", above a list of rows.
const ListToolbar = ({
  files,
  scope,
  dispatch,
}: {
  files: ReviewFile[];
  scope: string;
  dispatch: Dispatch;
}) => {
  const included = files.filter((f) => f.included).length;
  const all = included === files.length;
  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarLabel}>
        <Checkbox
          checked={all}
          indeterminate={included > 0 && !all}
          onChange={() =>
            dispatch({ type: 'setAllFiles', scope, included: !all })
          }
          label={all ? 'Deselect all' : 'Select all'}
        />
        <span>
          {included === files.length
            ? `All ${plural(files.length, 'file', 'files')} selected`
            : `${included} of ${files.length} selected`}
        </span>
      </div>
      <span className={styles.toolbarSpacer} />
      <SetAllDates
        disabled={included === 0}
        onApply={(date) => dispatch({ type: 'setAllDates', scope, date })}
      />
    </div>
  );
};

// The picker on an unsorted file's row.
const FileDestination = ({
  file,
  state,
  dispatch,
  options,
  onCreateAlbum,
}: {
  file: ReviewUnsortedFile;
  state: ReviewState;
  dispatch: Dispatch;
  options: DestinationOptions;
  onCreateAlbum: Sorting['onCreateAlbum'];
}) => (
  <DestinationPicker
    {...options}
    value={resolveDestination(state, file.destination, options.existingAlbums)}
    onChange={(destination) =>
      dispatch({ type: 'setDestination', fileKey: file.key, destination })
    }
    onCreateAlbum={(name) => onCreateAlbum(name, [file.key])}
    label={`Album for ${file.item.name}`}
  />
);

const destinationOptions = (
  state: ReviewState,
  existingAlbums: ExistingAlbum[],
): DestinationOptions => ({
  importAlbums: state.albums
    .filter((album) => album.included)
    .map((album) => ({ key: album.key, name: album.folder.name })),
  existingAlbums,
});

// Opens and closes a card from its title or its chevron.
const ExpandButton = ({
  expanded,
  name,
  onToggle,
}: {
  expanded: boolean;
  name: string;
  onToggle: () => void;
}) => (
  <button
    type="button"
    className={classnames(styles.expand, { [styles.expandOpen]: expanded })}
    onClick={onToggle}
    aria-label={expanded ? `Hide files in ${name}` : `Show files in ${name}`}
    aria-expanded={expanded}
  >
    <ChevronIcon />
  </button>
);

// ---------------------------------------------------------------------------
// Album card
// ---------------------------------------------------------------------------

// Prefer a photo for the cover (cheap to decode), then a video frame.
const coverFile = (files: ReviewFile[]) =>
  files.find((f) => f.item.localFile && fileKind(f.item.mimeType) === 'image')
    ?.item ??
  files.find((f) => f.item.localFile && fileKind(f.item.mimeType) === 'video')
    ?.item ??
  null;

export const AlbumCard = ({
  album,
  state,
  dispatch,
  expanded,
  onToggleExpanded,
  existingAlbums,
  onCreateAlbum,
}: {
  album: ReviewAlbum;
  state: ReviewState;
  dispatch: Dispatch;
  expanded: boolean;
  onToggleExpanded: () => void;
} & Sorting) => {
  const { name } = album.folder;
  // Its own files, then the unsorted files sent to it.
  const assigned = assignedFiles(state, album.key);
  const listed: ReviewFile[] = [...album.files, ...assigned];
  const counted = albumFiles(state, album.key);
  const fromUnsorted = assigned.filter((f) => f.included).length;
  const bytes = counted.reduce((sum, f) => sum + (f.item.size || 0), 0);
  const estimated = counted.filter(
    (f) => f.override == null && f.detected.source !== 'camera',
  ).length;
  const auto = albumAutoRange(state, album.key);
  const range = album.rangeOverride ?? auto;
  const cover = coverFile(listed);
  const options = destinationOptions(state, existingAlbums);
  const { unsupported } = album;
  const unsupportedNote = unsupported.length > 0 && (
    <span className={styles.unsupportedCount}>
      {` · ${unsupported.length} can’t be imported`}
    </span>
  );

  let meta: ReactNode = 'Won’t be imported';
  if (album.included && listed.length === 0 && unsupported.length > 0) {
    meta = (
      <span className={styles.unsupportedCount}>
        {`Nothing to import · ${plural(unsupported.length, 'file', 'files')} can’t be imported`}
      </span>
    );
  } else if (album.included && listed.length === 0) {
    meta = 'No memories yet';
  } else if (album.included) {
    meta = (
      <>
        {counted.length === listed.length
          ? plural(listed.length, 'memory', 'memories')
          : `${counted.length} of ${plural(listed.length, 'memory', 'memories')}`}
        {bytes > 0 && ` · ${formatBytes(bytes)}`}
        {!album.created &&
          fromUnsorted > 0 &&
          ` · ${fromUnsorted} from Unsorted`}
        {estimated > 0 && (
          <span className={styles.estimated}>
            {` · ${plural(estimated, 'estimated date', 'estimated dates')}`}
          </span>
        )}
        {unsupportedNote}
      </>
    );
  }

  return (
    <section
      className={classnames(styles.card, {
        [styles.cardOff]: !album.included,
        [styles.cardCreated]: !!album.created,
      })}
      aria-label={`Album ${name}`}
    >
      <div className={layout.albumHead}>
        {/* An album added in this review is removed, not skipped. */}
        {!album.created && (
          <Checkbox
            checked={album.included}
            onChange={() =>
              dispatch({ type: 'toggleAlbum', albumKey: album.key })
            }
            label={album.included ? `Skip ${name}` : `Import ${name}`}
          />
        )}
        {cover ? (
          <FileThumb file={cover} className={styles.cover} />
        ) : (
          <span className={classnames(styles.cover, styles.thumbIcon)}>
            <span className={classnames(styles.thumbIcon, styles.coverIcon)}>
              <BookIcon />
            </span>
          </span>
        )}
        <button
          type="button"
          className={styles.cardTitle}
          onClick={onToggleExpanded}
          aria-expanded={expanded}
        >
          <span className={styles.nameRow}>
            <span className={styles.albumName} title={name}>
              {name}
            </span>
            {album.created && (
              <span className={styles.createdTag}>Added by you</span>
            )}
          </span>
          <span className={styles.albumMeta}>{meta}</span>
        </button>
        <div className={styles.headRange}>
          <RangeChip
            range={range}
            auto={auto}
            overridden={!!album.rangeOverride}
            albumName={name}
            disabled={!album.included}
            onChange={(next) =>
              dispatch({
                type: 'setAlbumRange',
                albumKey: album.key,
                range: next,
              })
            }
          />
        </div>
        {album.created && (
          <button
            type="button"
            className={styles.removeButton}
            onClick={() =>
              dispatch({ type: 'removeAlbum', albumKey: album.key })
            }
            title="Remove album · its files go back to Unsorted"
            aria-label={`Remove ${name}. Its files go back to Unsorted`}
          >
            <CrossIcon />
          </button>
        )}
        <ExpandButton
          expanded={expanded}
          name={name}
          onToggle={onToggleExpanded}
        />
      </div>

      {expanded && album.included && (
        <div className={styles.cardBody}>
          {listed.length === 0 && unsupported.length === 0 ? (
            <p className={styles.emptyBody}>
              Choose this album for any file in Unsorted to add it here.
            </p>
          ) : (
            <>
              {listed.length > 0 && (
                <ListToolbar
                  files={listed}
                  scope={album.key}
                  dispatch={dispatch}
                />
              )}
              <div className={styles.rows}>
                {album.files.map((file) => (
                  <FileRow
                    key={file.key}
                    file={file}
                    scope={album.key}
                    dispatch={dispatch}
                  />
                ))}
                {assigned.length > 0 && album.files.length > 0 && (
                  <div className={styles.rowsLabel}>From Unsorted</div>
                )}
                {assigned.map((file) => (
                  <FileRow
                    key={file.key}
                    file={file}
                    scope={album.key}
                    dispatch={dispatch}
                    destination={
                      <FileDestination
                        file={file}
                        state={state}
                        dispatch={dispatch}
                        options={options}
                        onCreateAlbum={onCreateAlbum}
                      />
                    }
                  />
                ))}
                <UnsupportedRows
                  files={unsupported}
                  labelled={listed.length > 0}
                />
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Loose files: Unsorted, or the vault album they're headed to
// ---------------------------------------------------------------------------

type LooseGroup = {
  key: string;
  // Null for the files left unsorted.
  albumName: string | null;
  destination: Destination;
  files: ReviewUnsortedFile<ImportableFile>[];
};

// One list of loose files with its heading and bulk actions.
const LooseFilesSection = ({
  group,
  unsupported,
  dispatch,
  options,
  onCreateAlbum,
}: {
  group: LooseGroup;
  unsupported: UnsupportedFile[];
  dispatch: Dispatch;
  options: DestinationOptions;
  onCreateAlbum: Sorting['onCreateAlbum'];
}) => {
  const { files, albumName } = group;
  const selected = files.filter((f) => f.included);
  const bytes = selected.reduce((sum, f) => sum + (f.item.size || 0), 0);
  const title = albumName ?? 'Unsorted';

  return (
    <section aria-label={`${title} files`}>
      <div className={layout.unsortedHead}>
        <h3 className={layout.sectionTitle}>{title}</h3>
        <span className={layout.sectionMeta}>
          {plural(selected.length, 'memory', 'memories')}
          {bytes > 0 && ` · ${formatBytes(bytes)}`}
        </span>
        <span className={layout.spacer} />
        <div className={layout.unsortedActions}>
          {selected.length > 0 && (
            <span className={styles.bulkDestination}>
              <DestinationPicker
                {...options}
                value={group.destination}
                pillLabel={albumName ? 'Change Album' : 'Add all to Album'}
                onChange={(destination) =>
                  selected.forEach((file) =>
                    dispatch({
                      type: 'setDestination',
                      fileKey: file.key,
                      destination,
                    }),
                  )
                }
                onCreateAlbum={(name) =>
                  onCreateAlbum(
                    name,
                    selected.map((f) => f.key),
                  )
                }
                label={`Album for every selected file in ${title}`}
              />
            </span>
          )}
          <SetAllDates
            label="Set all dates"
            className={layout.pillButton}
            disabled={selected.length === 0}
            onApply={(date) =>
              selected.forEach((file) =>
                dispatch({
                  type: 'setFileDate',
                  scope: UNSORTED,
                  fileKey: file.key,
                  date,
                }),
              )
            }
          />
        </div>
      </div>
      <div className={layout.unsortedList}>
        <div className={styles.rows}>
          {files.map((file) => (
            <FileRow
              key={file.key}
              file={file}
              scope={UNSORTED}
              dispatch={dispatch}
            />
          ))}
          <UnsupportedRows files={unsupported} labelled={files.length > 0} />
        </div>
      </div>
    </section>
  );
};

// Files that came with no folder of their own. Those headed to an album
// already in the vault (an upload started from that album) are listed under
// its name; the rest under "Unsorted". Files sent to one of this import's
// albums are listed in that album's card instead.
export const UnsortedSection = ({
  state,
  dispatch,
  existingAlbums,
  onCreateAlbum,
}: {
  state: ReviewState;
  dispatch: Dispatch;
} & Sorting) => {
  const options = destinationOptions(state, existingAlbums);
  const groups: LooseGroup[] = [];
  const unsorted: LooseGroup = {
    key: 'unsorted',
    albumName: null,
    destination: { kind: 'unsorted' },
    files: [],
  };
  (state.unsorted as ReviewUnsortedFile<ImportableFile>[]).forEach((file) => {
    const destination = resolveDestination(
      state,
      file.destination,
      existingAlbums,
    );
    if (destination.kind === 'album') return;
    if (destination.kind === 'unsorted') {
      unsorted.files.push(file);
      return;
    }
    const key = `existing:${destination.albumId}`;
    const group = groups.find((g) => g.key === key);
    if (group) {
      group.files.push(file);
      return;
    }
    groups.push({
      key,
      albumName:
        existingAlbums.find((album) => album.id === destination.albumId)
          ?.name ?? '',
      destination,
      files: [file],
    });
  });
  // Files that can't be imported go with the unsorted ones, or with the only
  // list there is.
  const unsupported = state.unsortedUnsupported;
  if (
    unsorted.files.length > 0 ||
    (groups.length === 0 && unsupported.length > 0)
  ) {
    groups.push(unsorted);
  }
  if (groups.length === 0) return null;
  const last = groups[groups.length - 1];

  return (
    <>
      {groups.map((group) => (
        <LooseFilesSection
          key={group.key}
          group={group}
          unsupported={group === last ? unsupported : []}
          dispatch={dispatch}
          options={options}
          onCreateAlbum={onCreateAlbum}
        />
      ))}
    </>
  );
};

// ---------------------------------------------------------------------------
// Files that can't be imported
// ---------------------------------------------------------------------------

// Red banner above the lists; opens to name the files being left out.
export const UnsupportedBanner = ({
  skipped,
}: {
  skipped: { name: string }[];
}) => {
  const [open, setOpen] = useState(false);
  if (skipped.length === 0) return null;
  const types = Array.from(
    new Set(
      skipped
        .map((file) => extensionOf(file.name))
        .filter((ext): ext is string => !!ext),
    ),
  ).map((ext) => `.${ext}`);
  return (
    <div className={layout.banner}>
      <button
        type="button"
        className={layout.bannerHead}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span className={layout.bannerIcon}>
          <AlertCircleIcon />
        </span>
        <span className={layout.bannerText}>
          <span className={layout.bannerTitle}>
            {plural(skipped.length, 'file', 'files')} can’t be imported
          </span>
          <span>
            Unsupported file {types.length === 1 ? 'type' : 'types'}
            {types.length > 0 && `: ${types.join(', ')}`}
          </span>
        </span>
        <ChevronIcon
          className={classnames(layout.bannerChevron, {
            [layout.bannerChevronOpen]: open,
          })}
        />
      </button>
      {open && (
        <ul className={layout.bannerList}>
          {skipped.map((file, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${file.name}-${index}`}>{file.name}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
